import React, { createContext, useContext, useState, useCallback } from "react";
import { Snackbar, Alert } from "@mui/material";

interface Props {
  handle: <T>(fn: () => Promise<T>) => Promise<T | null>;
  setError: (message: string) => void;
  clearError: () => void;
  setSuccess: (message: string) => void;
  clearSuccess: () => void;
}

const MessageContext = createContext<Props>({
  handle: async () => null,
  setError: () => {},
  clearError: () => {},
  setSuccess: () => {},
  clearSuccess: () => {},
});

export const useMessageHandler = () => useContext(MessageContext);

export const MessageContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [error, setErrorState] = useState<string | null>(null);
  const [success, setSuccessState] = useState<string | null>(null);
  const [open, setOpen] = useState(false);


  const handle = async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    try {
      setErrorState(null);
      return await fn();
    } catch (err: any) {
      console.error("API Fehler:", err);
      setErrorState(err?.message || "Ein unbekannter Fehler ist aufgetreten.");
      setOpen(true);
      return null;
    }
  };

  const setError = useCallback((message: string) => {
    console.error("Fehler gesetzt:", message);
    setTimeout(() => {
      setErrorState(message);
      setOpen(true);
    }, 0);
  }, []);

  const clearError = useCallback(() => {
    console.error("clear Error:");
    setErrorState(null);
    setOpen(false);
  }, []);

  
  const setSuccess = useCallback((message: string) => {
    setTimeout(() => {
      setSuccessState(message);
      setOpen(true);
    }, 0);
  }, []);

  const clearSuccess = useCallback(() => {
    setSuccessState(null);
    setOpen(false);
  }, []);

  return (
    <MessageContext.Provider value={{ handle, setError, clearError, setSuccess, clearSuccess }}>
      {children}
      {open && error?.trim() && (
        <Snackbar
          open={open}
          onClose={clearError}
          autoHideDuration={null}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ zIndex: 9999 }}
        >
          <Alert onClose={clearError} severity="error" sx={{ width: "100%" }}>
            {error}
          </Alert>
        </Snackbar>
      )}
      {open && success?.trim() && (
        <Snackbar
          open={open}
          onClose={clearSuccess}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ zIndex: 9999 }}
        >
          <Alert onClose={clearSuccess} severity="success" sx={{ width: "100%" }}>
            {success}
          </Alert>
        </Snackbar>
      )}
    </MessageContext.Provider>
  );
};
